// RailsDynamicFormList
// Rails fields_forをReact的に表現するための共通コンポネント・カスタムフック
// 
//

import React, { useState } from 'react';
import { RailsFormElementNamePath } from './rails_form_element_name_path';
import { DeepReadonly } from './core';

/** 1行分のデータ構造 */
export interface RailsDynamicFormListRecord<T> {
  /** レコードID */
  id: number | undefined;

  /** 値 (複数のカラムがある場合はジェネリクスでオブジェクトを指定可能) */
  value: T | undefined;

  /** 表示順 */
  display_order: number | undefined;

  /** 削除フラグ */
  _destroy: boolean | undefined;

  /** このレコードを識別するための一意の値(idはundefinedの可能性があるためその代替)
   ** Reactコンポーネントkey属性などに利用
   */
  _uniqueKey: string;
}

/** データの参照・追加・削除・操作インターフェース
 ** 親子コンポーネント間で共有 
*/
export interface RailsDynamicFormListContext<T> {
  /** 値変更時 */
  setValue: (index: number, value: T) => void;

  /** 移動 */
  move: (params: { from: number, to: number }) => void;

  /** 指定行に挿入
   ** オプション引数省略で末尾に挿入 
   */
  add: (options?: { index?: number | undefined }) => void;

  /** 指定行の削除 */
  remove: (index: number) => void;

  /** 削除予約フラグの取り消し */
  clearRemoveFlag: (index: number) => void;

  /** レコードからレコードを識別するための一意な文字列を取得 */
  getUniqueKey: (record: DeepReadonly<RailsDynamicFormListRecord<T>>) => string;

  /** レコード識別キーでレコードを取得 */
  getRecordByUniqueKey: (uniqueKey: string) => DeepReadonly<RailsDynamicFormListRecord<T>> | undefined;

  /** レコード識別キーでレコードのインデックスを取得 */
  getRecordIndexByUniqueKey: (uniqueKey: string) => number | undefined;

  /** 一覧 */
  readonly records: DeepReadonly<RailsDynamicFormListRecord<T>[]>;
}

/** データの参照・追加・削除・操作インターフェース(実装) */
class RailsDynamicFormListContextImpl<T> implements RailsDynamicFormListContext<T> {
  /** 行データ設定 Reactステート */
  private readonly setRecords: React.Dispatch<React.SetStateAction<DeepReadonly<RailsDynamicFormListRecord<T>[]>>>;

  /** 行データ Reactステート */
  public readonly records: DeepReadonly<RailsDynamicFormListRecord<T>[]>;

  /** 新しい行挿入時の初期値取得関数 */
  private readonly getDefaultValue?: ((() => T) | undefined);

  /** 初期化 */
  public constructor(
    params:
      {
        records: DeepReadonly<RailsDynamicFormListRecord<T>[]>,
        setRecords: React.Dispatch<React.SetStateAction<DeepReadonly<RailsDynamicFormListRecord<T>[]>>>,
        getDefaultValue?: (() => T) | undefined,
      }
  ) {
    this.records = params.records;
    this.setRecords = params.setRecords;
    this.getDefaultValue = params.getDefaultValue;
  }

  /** 1行分のデフォルト値 */
  private createDefaultRecord(): DeepReadonly<RailsDynamicFormListRecord<T>> {
    const value = this.getDefaultValue?.();
    return {
      id: undefined,
      value: value as DeepReadonly<typeof value>,
      display_order: 0,
      _destroy: false,
      _uniqueKey: Math.random().toString(),
    }
  }

  /** 値変更時 */
  public setValue(index: number, value: T) {
    this.setRecords(prev => {
      const newValues = Array.from(prev);
      if (newValues[index]) {
        newValues[index] = {
          ...newValues[index],
          value: value as DeepReadonly<typeof value>
        }
      }
      return newValues;
    });
  }

  /** 移動 */
  public move({ from, to }: { from: number, to: number }) {
    this.setRecords(prev => {
      const newValues = Array.from(prev);

      // 移動元を取り出し、削除
      const [item] = newValues.splice(from, 1);

      // 取り出した要素を移動先に挿入
      if (item) {
        newValues.splice(to, 0, item);
      }

      return updateDisplayOrder(newValues);
    });
  }

  /** 指定行に挿入 undefinedで末尾追加 */
  public add(options?: { index?: number | undefined }) {
    this.setRecords(prev => {
      const index = options?.index;
      const newValues = Array.from(prev);
      // 末尾追加
      if (index == undefined || index < 0 || index >= newValues.length) {
        newValues.push(this.createDefaultRecord());
      }

      // 指定位置追加
      else {
        newValues.splice(index, 0, this.createDefaultRecord());
      }
      return updateDisplayOrder(newValues);
    });
  }

  /** 削除 */
  public remove(index: number) {
    this.setRecords(prev => {
      const newValues = Array.from(prev);

      if (newValues[index]) {
        // レコードIDがある場合は削除予約
        if (newValues[index].id) {
          newValues[index] = {
            ...newValues[index],
            _destroy: true,
          };
        }
        // まだレコードIDがない場合は即削除
        else {
          newValues.splice(index, 1);
        }
      }

      return updateDisplayOrder(newValues);
    });
  }

  /** 削除予約フラグの取り消し */
  public clearRemoveFlag(index: number) {
    this.setRecords(prev => {
      const newValues = Array.from(prev);

      if (newValues[index]) {
        newValues[index] = {
          ...newValues[index],
          _destroy: false,
        };
      }

      return updateDisplayOrder(newValues);
    });
  }

  /** レコードからレコードを識別するための一意な文字列 */
  public getUniqueKey(record: DeepReadonly<RailsDynamicFormListRecord<T>>) {
    return record._uniqueKey;
  }

  /** レコード識別キーでレコードを取得 */
  public getRecordByUniqueKey(uniqueKey: string): DeepReadonly<RailsDynamicFormListRecord<T>> | undefined {
    return this.records.find(record => this.getUniqueKey(record) == uniqueKey);
  }

  /** レコード識別キーでレコードのインデックスを取得 */
  public getRecordIndexByUniqueKey(uniqueKey: string): number | undefined {
    const index = this.records.findIndex(record => this.getUniqueKey(record) == uniqueKey);
    return index >= 0 ? index : undefined;
  }
}

/**  動的なフォーム部品の追加や削除などをコントロールするための Reactカスタムフック パラメータ */
export interface UseRailsDynamicFormListContextParams<T> {
  /** 初期表示する一覧データ */
  firstRows: DeepReadonly<RailsDynamicFormListRecord<T>[]>;

  /** 新しい行挿入時の初期値取得関数 */
  getDefaultValue?: () => T;
}

/** 動的なフォーム部品の追加や削除などをコントロールするための Reactカスタムフック 
 ** 親コンポーネントで初期化
*/
export function useRailsDynamicFormListContext<T>({ firstRows, getDefaultValue }: UseRailsDynamicFormListContextParams<T>): RailsDynamicFormListContext<T> {

  // 一覧データをステートで持つ
  const [records, setRecords] = useState<DeepReadonly<RailsDynamicFormListRecord<T>[]>>(() =>
    firstRows.map<DeepReadonly<RailsDynamicFormListRecord<T>>>(values => ({
      ...values,
      _uniqueKey: Math.random().toString(),
    }))
  );

  return new RailsDynamicFormListContextImpl<T>({ records, setRecords, getDefaultValue });

}

/** id、_destroy、display_orderのhidden要素を配置するコンポネントの Props */
export interface RailsDynamicFormListCommonElementsProps {
  /** id、_destroy、display_orderの情報 */
  record: RailsDynamicFormListRecord<unknown>;
  /** 何行目のデータか */
  index: number,
  /** fields_forのname属性パス構造データ */
  name: RailsFormElementNamePath;
}

/** レコードID(id)のhidden要素を配置するコンポネント */
export function RailsDynamicFormListIdElement({ record, index, name }: RailsDynamicFormListCommonElementsProps) {
  return <input type="hidden" name={name.make({ index, field: 'id' }).toString()} value={record.id} />
}

/** 削除フラグ(_destroy)のhidden要素を配置するコンポネント */
export function RailsDynamicFormListDestroyElement({ record, index, name }: RailsDynamicFormListCommonElementsProps) {
  return <input type="hidden" name={name.make({ index, field: '_destroy' }).toString()} value={record._destroy ? '1' : '0'} />
}

/** 表示順(display_order)のhidden要素を配置するコンポネント */
export function RailsDynamicFormListDisplaytOrderElement({ record, index, name }: RailsDynamicFormListCommonElementsProps) {
  return <input type="hidden" name={name.make({ index, field: 'display_order' }).toString()} value={record.display_order} />
}

/** 表示順(display_order) の更新 */
function updateDisplayOrder<T>(records: Readonly<RailsDynamicFormListRecord<T>[]>): RailsDynamicFormListRecord<T>[] {
  const reordered = Array.from(records);
  let cnt = 0;
  reordered.forEach((record) => {
    if (!record._destroy) {
      record.display_order = cnt++;
    }
  });

  return reordered;
};
